import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  FileText,
  Inbox,
  Settings,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  ChevronRight,
  ArrowLeft,
  Send,
  Mail,
  Radar,
  Radio,
  UserPlus,
  Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const navSections = [
  {
    label: 'WORK',
    items: [
      { path: '/backoffice', icon: Zap, label: 'Command', exact: true },
      { path: '/backoffice/prospects', icon: Users, label: 'Prospects' },
      { path: '/backoffice/outbox', icon: Send, label: 'Outbox' },
      { path: '/backoffice/inbox', icon: Mail, label: 'Inbox' },
    ],
  },
  {
    label: 'SCOUT',
    items: [
      { path: '/backoffice/signals', icon: Radio, label: 'Signals' },
      { path: '/backoffice/scout', icon: Radar, label: 'Scout' },
    ],
  },
  {
    label: 'MANAGE',
    items: [
      { path: '/backoffice/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/backoffice/templates', icon: FileText, label: 'Templates' },
      { path: '/backoffice/beta', icon: UserPlus, label: 'Beta' },
      { path: '/backoffice/settings', icon: Settings, label: 'Settings' },
    ],
  },
];

export const BackofficeLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-slate-900 border-b border-slate-800 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-orange-500" />
          <span className="font-bold text-sm text-white tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>BACKOFFICE</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-56 bg-slate-900 border-r border-slate-800 z-40
        flex flex-col
        transform transition-transform duration-200
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="h-14 flex-shrink-0 flex items-center gap-2 px-5 border-b border-slate-800">
          <ShieldCheck className="w-5 h-5 text-orange-500" />
          <span className="font-black text-sm text-white tracking-widest" style={{ fontFamily: 'Barlow Condensed' }}>BACKOFFICE</span>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-4" data-testid="backoffice-nav">
          {navSections.map(section => (
            <div key={section.label}>
              <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-slate-600 px-3 mb-1">{section.label}</p>
              <div className="space-y-0.5">
                {section.items.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-all
                      ${isActive(item.path, item.exact)
                        ? 'bg-slate-800 text-orange-400 font-semibold'
                        : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}
                    `}
                    data-testid={`bo-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-4 h-4" strokeWidth={1.5} />
                    <span>{item.label}</span>
                    {isActive(item.path, item.exact) && <ChevronRight className="w-3 h-3 ml-auto" />}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="flex-shrink-0 p-3 border-t border-slate-800">
          <Link
            to="/app"
            className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="bo-back-to-app"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to BidSentry
          </Link>
          <div className="px-3 py-1.5 mt-1">
            <p className="text-xs font-medium text-slate-300 truncate">{user?.name}</p>
            <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-500 hover:text-red-400 text-xs h-8"
            onClick={() => { logout(); navigate('/'); }}
            data-testid="bo-logout-btn"
          >
            <LogOut className="w-3 h-3 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <main className="lg:ml-56 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};
